import { Divider } from "@mui/material";
import React from "react";
import Article from "../../components/Article";
import TextLink from "../../components/TextLink";
import landingZoneImage from "../../assets/images/aws-landing-zone.png";

export const AwsLandingZonesControlTowerDetails = {
  articleTitle: "AWS Landing Zones with Control Tower",
  articleDate: "2025-07-03",
  articleAuthor: "Harrison Cannon",
  articleImage: landingZoneImage,
  articleBody: "Build a secure multi-account AWS foundation quickly using Control Tower, OUs, and guardrails.",
  articleSlug: "aws-landing-zones-control-tower"
};

function AwsLandingZonesControlTower() {
  function ArticleContent() {
    return (
      <div>
        <h2>What is a landing zone?</h2>

        <p>
          An AWS landing zone is the baseline multi-account environment that gives your teams a secure and scalable place to build workloads. It usually includes account structure, identity federation, logging, networking standards, and automated controls.
        </p>

        <p>
          <TextLink url="https://aws.amazon.com/controltower/" text="AWS Control Tower" /> packages this foundation so you can move quickly without building every component from scratch.
        </p>

        <Divider className="divider" />

        <h2>Why use Control Tower</h2>

        <p>
          Control Tower helps platform teams standardise governance while reducing manual account setup work. Instead of creating one-off accounts and policies, you define a repeatable model and apply it consistently.
        </p>

        <ul>
          <li>Automated account provisioning through Account Factory.</li>
          <li>Centralised guardrails powered by AWS Organizations and Service Control Policies.</li>
          <li>Audit-ready logging through organization-wide CloudTrail and AWS Config.</li>
          <li>A single governance dashboard for drift, compliance, and account enrollment status.</li>
        </ul>

        <Divider className="divider" />

        <h2>Core landing zone accounts</h2>

        <p>
          A practical setup starts with dedicated accounts for operations, security, and workloads:
        </p>

        <ul>
          <li><strong>Management account:</strong> Root organizational account, used sparingly for administration.</li>
          <li><strong>Log archive account:</strong> Centralised destination for CloudTrail logs and other immutable evidence.</li>
          <li><strong>Audit account:</strong> Security and compliance visibility, including cross-account read access.</li>
          <li><strong>Workload accounts:</strong> Application environments such as dev, test, and prod.</li>
        </ul>

        <p>
          This account separation limits blast radius, improves cost visibility, and supports least-privilege boundaries.
        </p>

        <Divider className="divider" />

        <h2>Designing your organizational units (OUs)</h2>

        <p>
          Define OUs around governance requirements, not team names. Team structures change often; control requirements tend to remain stable.
        </p>

        <pre>
          <code>
            {`Root
├── Security
│   ├── LogArchive
│   └── Audit
├── Infrastructure
│   ├── SharedServices
│   └── Networking
├── Sandbox
└── Workloads
    ├── Development
    ├── Staging
    └── Production`}
          </code>
        </pre>

        <p>
          Start with a small number of OUs and expand only when you have clear policy differences. Too many OUs early can make governance harder, not easier.
        </p>

        <Divider className="divider" />

        <h2>Guardrails and policy strategy</h2>

        <p>
          In Control Tower, guardrails can be preventive or detective. Preventive controls stop actions before they happen. Detective controls evaluate resources after deployment and report non-compliance.
        </p>

        <p>
          A strong baseline includes:
        </p>

        <ul>
          <li>Block disabling CloudTrail and AWS Config.</li>
          <li>Restrict unsupported AWS Regions for your compliance posture.</li>
          <li>Require encryption at rest for key data services.</li>
          <li>Detect public exposure patterns in storage and networking services.</li>
        </ul>

        <p>
          Supporting docs: <TextLink url="https://docs.aws.amazon.com/controltower/latest/userguide/guardrails.html" text="Control Tower controls reference" />, <TextLink url="https://docs.aws.amazon.com/organizations/latest/userguide/orgs_manage_policies_scps.html" text="Service Control Policies (SCPs)" />, and <TextLink url="https://docs.aws.amazon.com/config/latest/developerguide/managed-rules-by-aws-config.html" text="AWS Config managed rules" />.
        </p>

        <p>
          Example SCP that denies actions outside approved Regions:
        </p>

        <pre>
          <code>
            {`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "DenyOutsideApprovedRegions",
      "Effect": "Deny",
      "NotAction": [
        "iam:*",
        "organizations:*",
        "route53:*",
        "cloudfront:*",
        "support:*"
      ],
      "Resource": "*",
      "Condition": {
        "StringNotEquals": {
          "aws:RequestedRegion": [
            "eu-west-1",
            "eu-west-2"
          ]
        }
      }
    }
  ]
}`}
          </code>
        </pre>

        <Divider className="divider" />

        <h2>Identity and access model</h2>

        <p>
          Use IAM Identity Center (formerly AWS SSO) for human access. Define permission sets once, then assign them to groups across accounts and OUs.
        </p>

        <p>
          Keep machine-to-machine access separate. Workloads should use IAM roles with short-lived credentials, not shared long-lived keys.
        </p>

        <Divider className="divider" />

        <h2>Network baseline decisions</h2>

        <p>
          One of the earliest design choices is centralised versus distributed networking. Most teams start with a shared network account hosting ingress, egress, and inspection controls, then connect workload VPCs through Transit Gateway.
        </p>

        <p>
          Whatever model you choose, define it as code with clear standards for CIDR ranges, DNS patterns, and environment segmentation.
        </p>

        <Divider className="divider" />

        <h2>How to deploy a landing zone</h2>

        <p>
          Deploying a landing zone is best done in phases so governance and networking foundations are stable before application teams onboard.
        </p>

        <p>
          The official deployment path is documented in <TextLink url="https://docs.aws.amazon.com/controltower/latest/userguide/getting-started-with-control-tower.html" text="Control Tower getting started" /> and <TextLink url="https://docs.aws.amazon.com/controltower/latest/userguide/walkthrough-baseline-landing-zone.html" text="landing zone setup guidance" />.
        </p>

        <ol>
          <li><strong>Prepare your prerequisites:</strong> Confirm Identity Center integration, domain ownership for notifications, and billing ownership for account lifecycle.</li>
          <li><strong>Set up Control Tower:</strong> Launch Control Tower in your management account and validate the log archive and audit accounts are created correctly.</li>
          <li><strong>Define OUs and baseline guardrails:</strong> Create OUs for security, sandbox, and workloads, then apply mandatory preventive and detective controls.</li>
          <li><strong>Enable account vending:</strong> Configure Account Factory and standard account metadata so teams can request new accounts consistently.</li>
          <li><strong>Deploy shared networking:</strong> Provision core network services in a dedicated infrastructure account and validate connectivity patterns.</li>
          <li><strong>Onboard workloads gradually:</strong> Start with one low-risk team, test operations and incident flows, then scale to additional teams.</li>
        </ol>

        <h3>Phase 0: Pre-deployment inputs</h3>

        <ul>
          <li>Target OU model and account naming convention.</li>
          <li>Region strategy (single-region, dual-region, or global).</li>
          <li>Identity source and RBAC group mapping.</li>
          <li>Security baseline: logging retention, key management, and vulnerability tooling.</li>
          <li>Network baseline: CIDR allocations, egress model, DNS ownership, and connectivity to on-premises.</li>
        </ul>

        <h3>Phase 1: Bootstrap and validate</h3>

        <p>
          After Control Tower setup, validate CloudTrail and Config aggregations in your audit account, and test account enrollment before enabling broad workload onboarding.
        </p>

        <Divider className="divider" />

        <h2>Deployment checklist before go-live</h2>

        <ul>
          <li>CloudTrail and AWS Config are collecting across all enrolled accounts and Regions.</li>
          <li>Break-glass access and emergency escalation runbooks are tested.</li>
          <li>Cross-account roles for security, operations, and platform engineering are validated.</li>
          <li>Budget alarms, tagging policies, and chargeback dimensions are in place.</li>
          <li>Backup and recovery expectations are documented for shared services.</li>
          <li>Guardrail exception workflow is documented with approval owners.</li>
        </ul>

        <p>
          Deployment validation docs: <TextLink url="https://docs.aws.amazon.com/controltower/latest/userguide/enrollment-prerequisites.html" text="account enrollment prerequisites" />, <TextLink url="https://docs.aws.amazon.com/controltower/latest/userguide/how-controls-work.html" text="how controls are evaluated" />, and <TextLink url="https://docs.aws.amazon.com/securityhub/latest/userguide/securityhub-cloudwatch-events.html" text="Security Hub event integrations" />.
        </p>

        <Divider className="divider" />

        <h2>Automating deployments with IaC and AFT</h2>

        <p>
          Once your initial landing zone is running, treat every change as code. Use Terraform or AWS CDK pipelines to manage baseline resources and keep drift under control.
        </p>

        <p>
          For account provisioning at scale, many teams adopt <TextLink url="https://docs.aws.amazon.com/controltower/latest/userguide/aft-overview.html" text="Account Factory for Terraform (AFT)" />. AFT lets you codify account requests, account customizations, and OU-specific controls through pull requests.
        </p>

        <p>
          A common pattern is:
        </p>

        <ol>
          <li>Developer submits account request in Git.</li>
          <li>CI validates naming, tags, and policy requirements.</li>
          <li>AFT provisions the account and applies baseline customizations.</li>
          <li>Post-deploy checks verify guardrails, logging, and network attachments.</li>
        </ol>

        <p>
          Reference material: <TextLink url="https://docs.aws.amazon.com/controltower/latest/userguide/aft-getting-started.html" text="AFT getting started" />, <TextLink url="https://docs.aws.amazon.com/controltower/latest/userguide/account-factory.html" text="Account Factory basics" />, and <TextLink url="https://developer.hashicorp.com/terraform/language/modules" text="Terraform modules" />.
        </p>

        <h3>Example AFT account request</h3>

        <pre>
          <code>
            {`module "team_payments_prod" {
  source = "./modules/aft-account-request"

  account_name              = "payments-prod"
  account_email             = "aws-payments-prod@example.com"
  organizational_unit       = "Workloads/Production"
  sso_user_email            = "platform@example.com"
  additional_sso_user_email = ["security@example.com"]

  custom_fields = {
    owner_team   = "payments"
    environment  = "prod"
    data_profile = "confidential"
    cost_center  = "cc-1204"
  }
}`}
          </code>
        </pre>

        <h3>Example post-provision baseline with CDK</h3>

        <pre>
          <code>
            {`import * as cdk from "aws-cdk-lib";
import * as budgets from "aws-cdk-lib/aws-budgets";
import * as sns from "aws-cdk-lib/aws-sns";
import * as subscriptions from "aws-cdk-lib/aws-sns-subscriptions";

export class AccountBaselineStack extends cdk.Stack {
  constructor(scope: cdk.App, id: string, props?: cdk.StackProps) {
    super(scope, id, props);

    const alertsTopic = new sns.Topic(this, "BudgetAlertsTopic");
    alertsTopic.addSubscription(new subscriptions.EmailSubscription("finops@example.com"));

    new budgets.CfnBudget(this, "MonthlyCostBudget", {
      budget: {
        budgetName: "monthly-account-budget",
        budgetLimit: { amount: 2000, unit: "USD" },
        timeUnit: "MONTHLY",
        budgetType: "COST"
      },
      notificationsWithSubscribers: [
        {
          notification: {
            comparisonOperator: "GREATER_THAN",
            threshold: 80,
            thresholdType: "PERCENTAGE",
            notificationType: "ACTUAL"
          },
          subscribers: [{ address: alertsTopic.topicArn, subscriptionType: "SNS" }]
        }
      ]
    });
  }
}`}
          </code>
        </pre>

        <Divider className="divider" />

        <h2>Operations and lifecycle</h2>

        <p>
          Treat the landing zone as a product. You need ownership, versioning, release notes, and change control just like application code.
        </p>

        <ul>
          <li>Create account vending workflows for teams with approvals and tags.</li>
          <li>Automate baseline add-ons such as budget alarms, backup policies, and vulnerability scanning.</li>
          <li>Continuously monitor drift and remediate deviations quickly.</li>
          <li>Measure platform health with metrics like provisioning lead time and compliance coverage.</li>
        </ul>

        <Divider className="divider" />

        <h2>Common mistakes to avoid</h2>

        <ul>
          <li>Launching all workloads in one OU and trying to retrofit governance later.</li>
          <li>Using the management account for day-to-day operations.</li>
          <li>Creating broad administrator roles without role-based permission boundaries.</li>
          <li>Skipping documentation for account request flows, escalation paths, and guardrail exceptions.</li>
        </ul>

        <Divider className="divider" />

        <h2>Final thoughts</h2>

        <p>
          AWS Landing Zones with Control Tower give you a strong foundation for secure growth. The best outcomes come from combining the managed capabilities of Control Tower with clear platform ownership, automation, and pragmatic governance.
        </p>

        <p>
          Start simple, enforce a solid baseline, and iterate as your organization matures. That approach keeps teams fast while maintaining security and compliance.
        </p>
      </div>
    );
  }

  return <Article
    article={AwsLandingZonesControlTowerDetails}
    content={ArticleContent}
  />;
}

export default AwsLandingZonesControlTower;
